import { useState } from "react";
import { Link } from "react-router-dom";
import SectionHeading from "@/components/feature/SectionHeading";
import { posts } from "@/mocks/posts";
import { zones } from "@/mocks/zones";

type TabKey = "hot" | "read" | "new";

const tabs: { key: TabKey; label: string; icon: string }[] = [
  { key: "hot", label: "综合热度", icon: "ri-fire-line" },
  { key: "read", label: "阅读最多", icon: "ri-eye-line" },
  { key: "new", label: "最新整理", icon: "ri-time-line" },
];

function getZoneName(zoneId: string) {
  return zones.find((zone) => zone.id === zoneId)?.name ?? "专区";
}

function formatReads(count: number) {
  return count >= 10000 ? `${(count / 10000).toFixed(1)}万` : `${count}`;
}

export default function RankingBoard() {
  const [active, setActive] = useState<TabKey>("hot");

  const ranked = [...posts]
    .sort((a, b) => {
      if (active === "new") return a.date < b.date ? 1 : -1;
      if (active === "read") return b.readCount - a.readCount;
      const scoreA = a.readCount + (a.hot ? 20000 : 0);
      const scoreB = b.readCount + (b.hot ? 20000 : 0);
      return scoreB - scoreA;
    })
    .slice(0, 6);

  return (
    <section aria-labelledby="ranking-title" className="w-full bg-background-100 border-b border-background-200">
      <div className="w-full px-4 md:px-8 py-12 md:py-16">
        <SectionHeading
          id="ranking-title"
          eyebrow="Ranking"
          title="内容热榜"
          description="同一批内容换个维度排，得出的顺序并不一样。想快速知道哪几条在集中被讨论，看这里就够了。"
          action={
            <Link
              to="/ranking"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-foreground-950 text-background-50 text-sm whitespace-nowrap cursor-pointer hover:bg-primary-700 transition-colors"
            >
              完整榜单解读
              <i className="ri-arrow-right-line"></i>
            </Link>
          }
        />

        <div className="mt-8 inline-flex items-center gap-1 p-1 rounded-full border border-background-300 bg-background-50">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActive(tab.key)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                active === tab.key
                  ? "bg-primary-600 text-background-50"
                  : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
            </button>
          ))}
        </div>

        <ol className="mt-6 flex flex-col divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50 overflow-hidden">
          {ranked.map((post, index) => (
            <li key={post.id}>
              <Link
                to={`/post/${post.id}`}
                className="group flex items-center gap-4 md:gap-6 px-5 md:px-6 py-4 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span
                  className={`font-label text-2xl md:text-3xl w-10 md:w-12 shrink-0 ${
                    index === 0
                      ? "text-primary-600"
                      : index < 3
                        ? "text-accent-700"
                        : "text-foreground-300"
                  }`}
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block font-heading font-semibold text-base md:text-lg text-foreground-950 line-clamp-1 group-hover:text-primary-700 transition-colors">
                    {post.title}
                  </span>
                  <span className="mt-1 block text-xs text-foreground-500">
                    {getZoneName(post.zoneId)} · {post.date} · {post.author}
                  </span>
                </span>
                <span className="hidden sm:flex items-center gap-1 text-sm text-foreground-600 shrink-0">
                  <i className="ri-eye-line"></i>
                  {formatReads(post.readCount)}
                </span>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all"></i>
              </Link>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}